import React from 'react'
import AddButton from '../../components/IconButton'
import addIcon from '../../assets/icons/Addimg.svg'
import profileIcon from '../../assets/icons/profile-icon.svg'
import CustomTable from '../../components/Table'
import CustomTableHead from '../../components/Table/TableHead'
import CustomTableBody from '../../components/Table/TableBody'
import CustomTableRow from '../../components/Table/TableRow'
import CustomTableData from '../../components/Table/TableData'
import TablePagination from '../../components/Table/Pagination'
import CustomTableDataCTA from '../../components/Table/TableDataCTA'
import SearchIcon from '../../assets/icons/search.svg'
import Empty from '../../assets/icons/Empty'
import { ViewStaffsSkeletonLoader } from '../../components/Loader/ViewStaffsSkeletonLoader'
import AddArtisan from '../../components/Services/AddArtisans'
import SuccessModal from '../../components/Modal/Services/SuccessModal'
import { Artisan, Artisan as ArtisanInterface } from '../../interfaces/services'
import ArtisanService from '../../api/artisan-services'
import { handleErrorResponse } from '../../utils/handleErrorResponse'
import { showToast } from '../../utils/toast'
import DefaultButton from '../../components/Button/Button'

type ArtisansPageProps = {
  serviceId: string
  estateId: string
  onBack?: () => void
  isLoading: boolean
  initialServiceOptions?: { id: string; title: string }[]
}

function useDebounce<T>(value: T, delay = 400) {
  const [debounced, setDebounced] = React.useState(value)
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

export default function ArtisansPage({
  estateId,
  serviceId,
  onBack,
  isLoading,
  initialServiceOptions = [],
}: ArtisansPageProps) {
  const [query, setQuery] = React.useState('')
  const debouncedQuery = useDebounce(query, 400)

  const [loading, setLoading] = React.useState(false)
  const [showAdd, setShowAdd] = React.useState(false)
  const [showSuccessModal, setShowSuccessModal] = React.useState(false)
  const [artisanName, setArtisanName] = React.useState('')
  const [clientAdded, setClientAdded] = React.useState<ArtisanInterface[]>([])
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)
  const [artisanToDelete, setArtisanToDelete] = React.useState<any>(null)
  const [selectedArtisanId, setSelectedArtisanId] = React.useState<string | null>(null)

  const handleEditArtisan = (artisan: Artisan) => {
    setSelectedArtisanId(artisan.id)
    setShowAdd(true)
  }

  const { data: service } = ArtisanService.useGetServiceById({ estateId, serviceId })
  const displayName = service?.title || serviceId

  const { data, error, isValidating, mutate } = ArtisanService.useGetArtisans({
    page: 1,
    size: 15,
    serviceId,
    search: debouncedQuery,
    estateId,
  })

  const apiArtisans = React.useMemo(() => {
    const results =
      (data as any)?.results ?? (data as any)?.data?.results ?? (data as any)?.data ?? []
    return results.map((r: any) => ({
      id: r.id ?? r._id ?? String(Date.now()),
      name: r.fullName || r.name || 'Unnamed',
      phone: r.phone || 'NIL',
      email: r.email || 'NIL',
      serviceRequestCount: Number(r.serviceRequestCount ?? r.requests ?? 0) || 0,
      avatar: r.imageUrl ?? r.avatar ?? r.profileImage ?? profileIcon,
      serviceId: r.serviceId || serviceId,
    })) as ArtisanInterface[]
  }, [data, serviceId])

  const combinedList = React.useMemo(() => {
    const apiIds = new Set(apiArtisans.map((a) => a.id))
    const uniqueClientAdded = clientAdded.filter((a) => !apiIds.has(a.id))
    return [...uniqueClientAdded, ...apiArtisans]
  }, [clientAdded, apiArtisans])

  const combinedLoading = isLoading || loading || isValidating

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    return combinedList.filter(
      (a) =>
        (a.serviceId || '') === serviceId &&
        (!q ||
          a.name.toLowerCase().includes(q) ||
          (a.email || '').toLowerCase().includes(q) ||
          (a.phone || '').includes(q))
    )
  }, [combinedList, query, serviceId])

  function handleOpenAdd() {
    setSelectedArtisanId(null)
    setShowAdd(true)
  }

  function handleCloseAdd() {
    setSelectedArtisanId(null)
    setShowAdd(false)
  }

  function openDeleteModal(artisan: any) {
    setArtisanToDelete(artisan)
    setShowDeleteModal(true)
  }

  async function handleConfirmDelete() {
    if (!artisanToDelete) return

    try {
      const res = await ArtisanService.deleteArtisan({
        artisanId: artisanToDelete.id,
        serviceId: serviceId,
      })

      setClientAdded((prev) => prev.filter((a) => a.id !== artisanToDelete.id))
      setShowDeleteModal(false)
      setArtisanToDelete(null)
      mutate()
      showToast({ description: res.message, status: 'success' })
    } catch (err) {
      handleErrorResponse(err)
    }
  }

  async function handleAddContinue(returned: any) {
    try {
      if (!returned) throw new Error('No response received from server')
      if (!returned.id) throw new Error('Invalid response - missing artisan ID')

      const newArtisan: ArtisanInterface = {
        id: returned.id,
        name: returned.fullName ?? returned.name ?? 'Unnamed',
        phone: returned.phone ?? 'NIL',
        email: returned.email ?? 'NIL',
        serviceRequestCount: returned.serviceRequestCount ?? 0,
        avatar: returned.imageUrl ?? profileIcon,
        serviceId: returned.serviceId ?? serviceId,
      }

      setClientAdded((prev) => [newArtisan, ...prev])
      setArtisanName(newArtisan.name)
      setShowSuccessModal(true)
      setShowAdd(false)
    } catch (error) {
      console.error('Failed to add artisan:', error)
      alert('Failed to add artisan. Please try again.')
    }
  }

  function handleHeaderBack() {
    if (showSuccessModal) {
      setShowSuccessModal(false)
      return
    }
    if (showAdd) {
      setShowAdd(false)
      return
    }
    onBack?.()
  }

  function handleKeyActivate(e: React.KeyboardEvent, fn: () => void) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      fn()
    }
  }

  if (combinedLoading) return <ViewStaffsSkeletonLoader />

  return (
    <>
      <div className="min-h-screen bg-white">
        <div className="w-full">
          <header className="flex items-center gap-4 mb-10">
            <button
              type="button"
              aria-label="Back"
              onClick={handleHeaderBack}
              className="p-2 rounded-md hover:bg-gray-100 flex gap-4 items-center"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="text-lg text-gray-700 font-medium">Services</div>
            </button>

            <div className="ml-3 px-3 py-2 rounded-md bg-black text-white text-lg font-medium">
              {showAdd ? 'Add Artisan' : displayName}
            </div>

            <div className="ml-auto flex items-center gap-8">
              {!showAdd && !showSuccessModal && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={handleOpenAdd}
                  onKeyDown={(e) => handleKeyActivate(e, handleOpenAdd)}
                >
                  <AddButton
                    buttonStyle="w-[220px] bg-white"
                    icon={<img src={addIcon} alt="Add icon" className="rounded-lg" />}
                    iconClassName="w-12 h-12"
                  >
                    <div className="flex flex-col justify-center px-4">
                      <p className="text-black font-medium text-lg">Add Artisan</p>
                    </div>
                  </AddButton>
                </div>
              )}
            </div>
          </header>

          {!showAdd && !showSuccessModal && (
            <div className="mb-8">
              <div className="w-full flex bg-gray-100 rounded-lg items-center px-2 relative">
                <img
                  src={SearchIcon}
                  alt="Search"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                />
                <input
                  value={query}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setQuery(e.target.value)
                  }
                  placeholder="Search Artisan"
                  className="py-4 pl-10 w-full bg-transparent h-full"
                  aria-label="Search Artisan"
                />
              </div>
            </div>
          )}

          {showAdd ? (
            <AddArtisan
              onCancel={handleCloseAdd}
              onContinue={handleAddContinue}
              serviceId={serviceId}
              estateId={estateId}
              initialServiceOptions={initialServiceOptions}
              artisanId={selectedArtisanId}
              mutate={mutate}
              key={selectedArtisanId || 'create'}
            />
          ) : (
            <>
              <CustomTable>
                <CustomTableHead
                  dataList={[
                    'Name of Artisan',
                    'Phone number',
                    'Email address',
                    'Request',
                    'More',
                  ]}
                />
                {filtered.length === 0 ? (
                  <div className="flex flex-col gap-5 justify-center items-center sm:justify-center sm:items-center h-96 w-auto">
                    <Empty />
                    <p className="text-sm font-semibold">No results found</p>
                  </div>
                ) : (
                  <CustomTableBody>
                    {filtered.map((a) => (
                      <CustomTableRow key={a.id}>
                        <CustomTableData
                          dataLabel="Name of Artisan"
                          text={a.name}
                          imageUrl={a.avatar || profileIcon}
                        />
                        <CustomTableData dataLabel="Phone number" text={a.phone} />
                        <CustomTableData dataLabel="Email address" text={a.email} />
                        <CustomTableData
                          dataLabel="Request"
                          text={`${a.serviceRequestCount ?? 0} ${
                            (a.serviceRequestCount ?? 0) === 1 ? 'request' : 'requests'
                          }`}
                        />
                        <CustomTableDataCTA dataLabel="More" text="...">
                          <div className="flex flex-col bg-white shadow rounded w-40 text-left">
                            <p
                              onClick={() => handleEditArtisan(a)}
                              className="hover:bg-gray-200 p-3 border-t cursor-pointer"
                            >
                              Edit/view details
                            </p>
                            <p
                              className="hover:bg-gray-200 p-3 border-t text-red-500 cursor-pointer"
                              onClick={() => openDeleteModal(a)}
                            >
                              Delete artisan
                            </p>
                          </div>
                        </CustomTableDataCTA>
                      </CustomTableRow>
                    ))}
                  </CustomTableBody>
                )}
              </CustomTable>

              {filtered.length > 0 && (
                <div className="flex justify-end mt-4">
                  <TablePagination
                    totalPages={data?.totalPages ?? 1}
                    currPage={data?.page ?? 1}
                    setCurrentPage={() => {}}
                  />
                </div>
              )}
            </>
          )}

          <SuccessModal
            open={showSuccessModal}
            name={artisanName}
            onAddNew={() => {
              setShowSuccessModal(false)
              setShowAdd(true)
            }}
            onClose={() => setShowSuccessModal(false)}
          />
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[400px] shadow-xl">
            <h2 className="text-xl font-semibold mb-4">Delete Artisan</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <strong>{artisanToDelete?.name}</strong>?
            </p>
            <div className="flex justify-end gap-4">
              <DefaultButton
                text="Cancel"
                className="sm:w-52 grow sm:grow-0 hover:grow-[9] hover:sm:grow-0 text-gray-200 bg-transparent"
                onClick={() => setShowDeleteModal(false)}
                buttonType="secondary"
              />
              <DefaultButton
                text="Delete"
                className="sm:w-52 grow sm:grow-0 hover:grow-[9] hover:sm:grow-0"
                onClick={handleConfirmDelete}
                isLoading={loading}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}
